import { useCallback, useMemo, useState } from 'react';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import {
  useCreateSongPublishMutation,
  useGetServiceConnectionsQuery,
  useGetSongPublishesQuery,
} from '../../../../redux/api/fymAPI';
import { CatalogItem, ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useOpenSongPage } from '../../../smartLinks/catalog/song/page/useOpenSongPage';
import { useSoundCloudConnect } from '../../soundcloud/useSoundCloudConnect';
import { soundCloudUploadReturnTo } from '../../soundcloud/soundCloudReturnTo';
import { SOUNDCLOUD_EXTRAS_SERVICE } from '../servicesPanelServices';
import type { StoreLogin } from '../servicesPanelRows';
import { useUploadReturn } from '../useUploadReturn';
import {
  soundCloudExtraView,
  songMasterStateOf,
  type SoundCloudExtraAction,
  type SoundCloudExtraView,
} from './soundCloudExtraRules';

/** The login of the SoundCloud card. A card with no artist counts as none: it cannot upload. */
export function storeLoginOf(connection: ServiceConnection | undefined): StoreLogin {
  if (connection === undefined) {
    return 'none';
  }
  if (connection.status === ServiceConnectionStatus.expired) {
    return 'expired';
  }
  return connection.status === ServiceConnectionStatus.connected && connection.artist !== null ? 'connected' : 'none';
}

/** The newest SoundCloud record of the song (the list is the song's own). */
export function newestSoundCloudPublish(publishes: SerializedSongPublish[] | undefined): SerializedSongPublish | null {
  const own = (publishes ?? []).filter((publish) => publish.channel === 'soundcloud');
  if (own.length === 0) {
    return null;
  }
  return own.reduce((newest, one) => (one.createdAt > newest.createdAt ? one : newest));
}

/** The upload dialog of the item: `upload` (c3, c5) or `status` (c6). */
export type UploadDialogMode = 'upload' | 'status';

/**
 * The data and the actions of "SoundCloud extras": the SoundCloud login, the uploads of the song,
 * the upload dialog and the cancel question. The socket event `song.publish.updated` reads the
 * uploads again (tag `SongPublish`); there is no poll while the socket is up.
 */
export function useSoundCloudExtras({
  accountId,
  item,
  readOnly,
}: {
  accountId: number;
  item: CatalogItem;
  readOnly: boolean;
}) {
  const { data: connections } = useGetServiceConnectionsQuery({ accountId }, { skip: readOnly });
  const { data: publishes } = useGetSongPublishesQuery({ accountId, catalogItemUuid: item.uuid }, { skip: readOnly });
  const login = useMemo(
    () => storeLoginOf(connections?.connections?.find((one) => one.integrationType === SOUNDCLOUD_EXTRAS_SERVICE)),
    [connections]
  );
  const publish = useMemo(() => newestSoundCloudPublish(publishes?.publishes), [publishes]);
  const master = songMasterStateOf(item.song);
  const view: SoundCloudExtraView = useMemo(
    () => soundCloudExtraView({ publish, login, master, readOnly }),
    [login, master, publish, readOnly]
  );

  const [dialog, setDialog] = useState<UploadDialogMode | null>(null);
  const [cancelling, setCancelling] = useState<SerializedSongPublish | null>(null);
  const [resend, resendState] = useCreateSongPublishMutation();
  const soundCloud = useSoundCloudConnect({ accountId });
  const openSong = useOpenSongPage();

  const openUpload = useCallback(() => setDialog('upload'), []);
  useUploadReturn(!readOnly, openUpload);

  const connect = useCallback(() => {
    soundCloud.connect(soundCloudUploadReturnTo(window.location.href)).catch(console.error);
  }, [soundCloud]);

  const run = useCallback(
    (action: SoundCloudExtraAction) => {
      switch (action) {
        case 'upload':
          setDialog('upload');
          return;
        case 'status':
        case 'change':
          setDialog('status');
          return;
        case 'connect':
        case 'reconnect':
          connect();
          return;
        case 'cancel':
          setCancelling(publish);
          return;
        case 'addMaster':
          openSong(item.uuid, 'files');
          return;
        case 'retry':
          // "Try again" sends the same upload again (design c6): same title and sharing, now.
          if (publish !== null) {
            resend({
              accountId,
              body: {
                catalogItemUuid: publish.catalogItemUuid,
                channel: publish.channel,
                timing: 'now',
                publishAt: null,
                title: publish.title ?? '',
                sharing: publish.sharing,
              },
            })
              .unwrap()
              .catch(() => setDialog('status'));
          }
          break;
        case 'open':
          // An address: the item draws it as a link, and the browser opens it.
          break;
      }
    },
    [accountId, connect, item.uuid, openSong, publish, resend]
  );

  return {
    view,
    publish,
    run,
    isBusy: soundCloud.isConnecting || resendState.isLoading,
    dialog,
    closeDialog: useCallback(() => setDialog(null), []),
    cancelling,
    closeCancel: useCallback(() => setCancelling(null), []),
    connect,
  };
}
